import React from 'react';
import { NextPage } from 'next';
import {
  Button,
  ButtonVariant,
  Grid, Spacing, SpacingName, Symbol, Typography, TypographyName,
} from '@doveit/lego';
import AppLayout from '../client/layouts/app-layout/AppLayout';
import PageLayout from '../client/layouts/page-layout/PageLayout';
import { withError, withGetServerSideError } from '../client/hocs/with-server-side-error/withServerSideError';
import PageSection from '../client/components/page-section/PageSection';
import Hero from '../client/templates/common/hero/Hero';
import { TextAlign, WrapperSize } from '../client/types';
import variables from '../client/themes/variables';
import SectionDivider from '../client/components/section-divider/SectionDivider';
import PageHead from '../client/components/page-head/PageHead';
import * as styles from '../client/templates/press/Press.style';
import { TRAY_ARROW_DOWN } from '../client/themes/icons';
import initGraphQLClient from '../client/graphql/initGraphQLClient';
import { PressArticle, PressRelease } from '../domain/graphcms';
import { LATEST_FEATURED_PRESS_ARTICLES, LATEST_PRESS_ARTICLES, PRESS_RELEASES } from '../client/graphql/queries/press';
import { formatPublishDate } from '../client/graphql/mappers/articleMappers';

export interface PressProps {
  featuredPressArticles: PressArticle[],
  latestPressArticles: PressArticle[],
  pressReleases: PressRelease[],
}

export const PRESS_ARTICLE_PREVIEW_WIDTH = 240;

export const Press: NextPage<PressProps> = ({
  featuredPressArticles,
  latestPressArticles,
  pressReleases,
}) => (
  <AppLayout>
    <PageHead
      SEO={{
        title: 'Notizie e Comunicati Stampa',
        description: 'Scopri le ultime notizie e comunicati stampa su Dove.it, l\'agenzia immobiliare digitale che rivoluziona il mercato con innovazione e servizi a zero commissioni.',
      }}
    />
    <PageLayout>
      <Hero title="Notizie e comunicati stampa agenzia immobiliare Dove.it" />
      <SectionDivider />
      <PageSection
        padding={[SpacingName.BIGGER, 0, 0]}
      >
        <PageSection.Wrapper maxWidth={WrapperSize.M}>
          <PageSection.Title color="brand.primary">
            Dicono di noi
          </PageSection.Title>
          <PageSection.Block>
            {featuredPressArticles.map((pressArticle) => (
              <div
                data-ref="featured-article-card"
                key={pressArticle.id}
                css={styles.featuredArticleCard}
              >
                <Grid gutter={SpacingName.SMALL}>
                  <Grid.Unit size={{ MD: 6 / 9 }}>
                    <Typography.H3 tagName="h3">
                      <a
                        href={pressArticle.link}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {pressArticle.title}
                      </a>
                    </Typography.H3>
                    <Spacing margin={[SpacingName.SMALLER, 0, SpacingName.SMALL]}>
                      <Typography.BODY color="neutrals.dark">
                        {pressArticle.abstract}
                      </Typography.BODY>
                    </Spacing>
                    <Typography.SMALL color="neutrals.medium">
                      {pressArticle.newspaper} - {formatPublishDate(pressArticle.publishedDate)}
                    </Typography.SMALL>
                  </Grid.Unit>
                  {pressArticle.logo && (
                    <Grid.Unit size={{ MD: 3 / 9 }}>
                      <div css={styles.featuredLogoContainer}>
                        <img
                          src={pressArticle.logo.url}
                          alt={`${pressArticle.newspaper}-logo`}
                          css={styles.featuredLogoImage}
                        />
                      </div>
                    </Grid.Unit>
                  )}
                </Grid>
              </div>
            ))}
          </PageSection.Block>
        </PageSection.Wrapper>
      </PageSection>
      <PageSection padding={[SpacingName.BIGGER, 0]}>
        <PageSection.Wrapper maxWidth={WrapperSize.M}>
          <PageSection.Title
            color="neutrals.medium"
            size={TypographyName.H3}
          >
            Ultime News
          </PageSection.Title>
          <PageSection.Block>
            <Grid gutter={SpacingName.SMALLER}>
              {latestPressArticles.map((pressArticle) => (
                <Grid.Unit
                  key={pressArticle.id}
                  size={{ MD: 1 / 2 }}
                >
                  <div
                    data-ref="article-card"
                    css={styles.articleCard}
                  >
                    <Typography.H4 tagName="h3">
                      <a
                        href={pressArticle.link}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {pressArticle.title}
                      </a>
                    </Typography.H4>
                    <Spacing margin={[SpacingName.SMALLER, 0, 0]}>
                      <Typography.SMALL color="neutrals.medium">
                        {pressArticle.newspaper} - {formatPublishDate(pressArticle.publishedDate)}
                      </Typography.SMALL>
                    </Spacing>
                  </div>
                </Grid.Unit>
              ))}
            </Grid>
          </PageSection.Block>
        </PageSection.Wrapper>
      </PageSection>
      <PageSection
        padding={[SpacingName.BIGGER, 0, SpacingName.BIGGEST]}
        backgroundGradient={variables.sectionBackgroundGradient}
      >
        <PageSection.Wrapper maxWidth={WrapperSize.M}>
          <PageSection.Title color="brand.primary">
            Comunicati stampa
          </PageSection.Title>
          <PageSection.Block>
            {pressReleases.map((pressRelease) => (
              <a
                data-ref="press-release-card"
                key={pressRelease.id}
                css={styles.pressReleaseCard}
                href={pressRelease.release.url}
              >
                <Typography.SMALL color="neutrals.medium">
                  {formatPublishDate(pressRelease.publishedDate, 'MM/yyyy')}
                </Typography.SMALL>
                <Typography.H4 tagName="h3">
                  {pressRelease.title}
                </Typography.H4>
                <div css={styles.pressReleaseCardIcon}>
                  <Symbol
                    size={40}
                    path={TRAY_ARROW_DOWN}
                  />
                </div>
              </a>
            ))}
          </PageSection.Block>
        </PageSection.Wrapper>
      </PageSection>
      <PageSection
        padding={[SpacingName.BIGGER, 0]}
        backgroundColor="neutrals.lighter"
      >
        <PageSection.Wrapper>
          <Grid gutter={SpacingName.MEDIUM}>
            <Grid.Unit size={{ MD: 1 / 2 }}>
              <div
                css={styles.box}
                data-ref="contacts-section"
              >
                <Typography.H3
                  tagName="h2"
                  textAlign={TextAlign.CENTER}
                >
                  Contatti
                </Typography.H3>
                <Spacing margin={[SpacingName.MEDIUM, 0, 0]}>
                  <Typography.BODY color="neutrals.dark">
                    Contatta il nostro ufficio stampa
                  </Typography.BODY>
                </Spacing>
                <Spacing margin={[SpacingName.SMALLER, 0, 0]}>
                  <Typography.H4 color="neutrals.dark">
                    <a data-ref="press-email" href="mailto:press@dove.it?subject=Richiesta informazioni">
                      press@dove.it
                    </a>
                  </Typography.H4>
                </Spacing>
              </div>
            </Grid.Unit>
            <Grid.Unit size={{ MD: 1 / 2 }}>
              <div
                css={styles.box}
                data-ref="press-kit-section"
              >
                <Typography.H3
                  tagName="h2"
                  textAlign={TextAlign.CENTER}
                >
                  Press Kit
                </Typography.H3>
                <Spacing margin={[SpacingName.MEDIUM, 0, 0]}>
                  <Typography.BODY color="neutrals.dark" textAlign={TextAlign.CENTER}>
                    Scarica le risorse per la stampa, web e comunicazione
                  </Typography.BODY>
                </Spacing>
                <Spacing margin={[SpacingName.SMALL, 0, 0]}>
                  <Button
                    icon={TRAY_ARROW_DOWN}
                    label="Scarica Press Kit"
                    variant={ButtonVariant.OUTLINE}
                    href="/press_kit.zip"
                  />
                </Spacing>
              </div>
            </Grid.Unit>
          </Grid>
        </PageSection.Wrapper>

      </PageSection>
    </PageLayout>
  </AppLayout>
);

export const getServerSideProps = withGetServerSideError<PressProps>(async () => {
  const graphQLClient = initGraphQLClient();
  const { data: { featuredPressArticles } } = await graphQLClient.query<{ featuredPressArticles: PressArticle[] }>({
    query: LATEST_FEATURED_PRESS_ARTICLES,
    variables: {
      size: 3,
      logoWidth: PRESS_ARTICLE_PREVIEW_WIDTH,
    },
  });

  const excludedIds = featuredPressArticles.map((article) => article.id);

  const [
    { data: { latestPressArticles } },
    { data: { pressReleases } },
  ] = await Promise.all([
    await graphQLClient.query<{ latestPressArticles: PressArticle[] }>({
      query: LATEST_PRESS_ARTICLES,
      variables: {
        size: 4,
        excludedIds,
      },
    }),
    await graphQLClient.query<{ pressReleases: PressRelease[] }>({
      query: PRESS_RELEASES,
      variables: { size: 5 },
    }),
  ]);

  return {
    props: {
      featuredPressArticles,
      latestPressArticles,
      pressReleases,
    },
  };
});

export default withError(Press);
